import { useState, useEffect, useMemo, useRef } from 'react'
import Formsy from 'formsy-react'

import { Button, IconButton, Table, TableContainer, TableHead, TableBody, Paper, 
        DialogTitle, DialogContent,  DialogActions } from '@mui/material'

import { Add, Delete, Edit } from '@mui/icons-material'

import { D, Loading, SearchField, TRow, TCell, TextFieldFormsy, ConfirmDelete } from '../include'


// ==========================================
// formulario de ediçao/adiçao de palavra de atençao
// ==========================================
const EditAttentionWord = ({ row, edit, api, alert, dialogClose }) => {
  // Inicializa o estado com o valor do banco (se houver) ou string vazia
  const [nome, setNome] = useState(row ? row.texto : '');

  const handleSave = () => {
    if (!edit) {
      api.post('/AttWord/', { texto: nome })
        .then(r => {
          dialogClose();
          alert(r.msg);
        })
        .catch(e => alert(e.msg));
    } else {
      api.put(`/AttWord/${row.id}`, { id: row.id, texto: nome })
        .then(r => {
          dialogClose();
          alert(r.msg);
        })
        .catch(e => alert(e.msg));
    }
  };

  return (
    <Formsy onValidSubmit={() => null}>
      <DialogTitle style={{ minWidth: '400px' }}>
        {edit ? 'Editar Palavra de atenção' : 'Adicionar Palavra de atenção'}
      </DialogTitle>
      
      <DialogContent>
        <D h="1rem" />
        <TextFieldFormsy 
          value={nome} 
          onChange={v => setNome(v)} 
          type="text" 
          name="name" 
          label="Nome" 
          variant="outlined" 
          required 
        />
        <D h="2rem" />
      </DialogContent>
      
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>
          Cancelar
        </Button>
        <Button variant="outlined" color="primary" onClick={handleSave}>
          Salvar
        </Button>
      </DialogActions>
    </Formsy>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: lista de palavras de atençao
// ==========================================
export const AttentionWord = ({ api, alert, dialog, dialogClose, permission }) => {
  const [searchField, setSearchField] = useState('');
  const [dataTable, setDataTable] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 1. Criar a referência para rastrear a primeira renderização
  const isFirstRender = useRef(true);

  const updateData = () => {
    setIsLoading(true);
    api.get('/AttWords', { params: { log: true } })
      .then(r => {
        setDataTable(r);
        setIsLoading(false);
      })
      .catch(e => {
        alert("Erro ao buscar dados.");
        setIsLoading(false);
      });
  };

  // Substitui o componentDidMount
  useEffect(() => {
    if (isFirstRender.current) {
      updateData();
      isFirstRender.current = false; // Desativa a flag após a 1ª chamada
    }    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditDialog = (row = null, isEdit = false) => {
    dialog(
      <EditAttentionWord 
        edit={isEdit} 
        row={row} 
        api={api} 
        alert={alert} 
        dialogClose={() => {
          dialogClose();
          updateData();
        }} 
      />
    );
  };

  const openDeleteDialog = (row) => {
    dialog(
      <ConfirmDelete
        msg="Tem certeza que deseja excluir a palavra de atenção:"
        itemName={row.texto}
        onCancel={dialogClose}
        onConfirm={() => {
          api.delete(`/AttWord/${row.id}`)
            .then(r => {
              alert(r.msg);
              updateData(); 
              dialogClose();
            })
            .catch(e => alert(e.msg || 'Erro ao excluir a palavra de atenção.'));
        }}
      />
    );
  };

  // Filtra os dados na memória de forma otimizada
  const filteredData = useMemo(() => {
    if (!searchField) return dataTable;
    
    const searchLower = searchField.toLowerCase().replace(/\s/g, '');
    return dataTable.filter(row => {
      const rowValues = Object.values(row).map(v => String(v)).join('');
      return rowValues.toLowerCase().replace(/\s/g, '').includes(searchLower);
    });
  }, [dataTable, searchField]);

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      {permission === 'w' && (
        <>
          <D className="row w aF">
            <SearchField 
              value={searchField} 
              onChange={v => setSearchField(v)} 
            />
            <D e />
            <D size="2rem" />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => openEditDialog(null, false)}
            >
              <Add /> Palavra de Atenção
            </Button>
          </D>
          <D h="1rem" />
        </>
      )}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow sx={{ height: '3rem', background: '#ccc' }}>
                {permission === 'w' && <TCell align="center">Editar</TCell>}
                {permission === 'w' && <TCell align="center">Excluir</TCell>}
                <TCell width="100%">Palavra de Atenção ({dataTable.length} palavras)</TCell>
              </TRow>
            </TableHead>
            
            <TableBody>
              {isLoading ? (
                <TRow>
                  <TCell component="th" align="center" colSpan={3}>
                    <Loading />
                  </TCell>
                </TRow>
              ) : filteredData.length > 0 ? (
                filteredData.map(row => (
                  <TRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {permission === 'w' && (
                      <TCell align="center" component="th">
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openEditDialog(row, true)}
                        >
                          <Edit />
                        </IconButton>
                      </TCell>
                    )}

                    {permission === 'w' && (
                      <TCell align="center" component="th">
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openDeleteDialog(row)}
                        >
                          <Delete />
                        </IconButton>
                      </TCell>
                    )}
                    <TCell component="th">{row.texto}</TCell>
                  </TRow>
                ))
              ) : (
                <TRow>
                  <TCell component="th" align="center" colSpan={3}>
                    Nenhuma palavra encontrada
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};